// Shared report catalog used by report dashboards, report shell nav, and site map.

export function buildReportContext(user, viewAs) {
  const tenant = user.tenant
  const tenant_type = user.types[tenant]
  const telematics_type = user.telematics_types[tenant]
  const features = user.features[tenant] || {}
  const isEffectiveAdmin = user.role === 'admin' && viewAs !== 'non-admin'
  const isTbn = tenant_type === 'TBN'
  const isCmFleet = tenant_type === 'CM-FLEET'
  const isFasttrak = tenant_type === 'FASTTRAK'
  const isParentTbn = isTbn && tenant.indexOf('parent') !== -1

  let restrictedRole = null
  if (!isEffectiveAdmin && !isCmFleet) {
    const r = user.role
    if (r === 'garage') restrictedRole = 'garage'
    else if (isParentTbn && r !== 'customer-admin') restrictedRole = 'tbn-parent'
    else if (r === 'manager') restrictedRole = 'manager'
  }

  const showActuals = (() => {
    if (restrictedRole === 'garage') return false
    if (isCmFleet) return false
    if (restrictedRole === 'manager' || restrictedRole === 'tbn-parent') return true
    return !!features.actuals_enabled
  })()

  const showPlanned = (() => {
    if (restrictedRole === 'manager' || restrictedRole === 'tbn-parent') return false
    if (isCmFleet) return false
    return true
  })()

  const showOtp = (() => {
    if (restrictedRole) return false
    return !!features.views_reports_otp_report_enabled || isCmFleet
  })()

  const showMisc = (() => {
    if (restrictedRole === 'garage' || restrictedRole === 'manager') return false
    if (isCmFleet) return false
    return true
  })()

  const hasReportsAccess = (() => {
    if (isCmFleet) return true
    if (isFasttrak) return false
    const noReportsRoles = ['dispatch', 'driver', 'booking-customer', 'customer-care', 'wash-bay']
    if (noReportsRoles.includes(user.role)) return false
    if (isEffectiveAdmin) return true
    if (user.role === 'garage') return !isTbn
    return !!features.views_reports_enabled
  })()

  return {
    user,
    tenant,
    tenant_type,
    telematics_type,
    features,
    isEffectiveAdmin,
    isTbn,
    isCmFleet,
    isFasttrak,
    isParentTbn,
    restrictedRole,
    showActuals,
    showPlanned,
    showOtp,
    showMisc,
    hasReportsAccess,
  }
}

export function getActualsCards(ctx) {
  const { restrictedRole, isTbn } = ctx
  if (restrictedRole === 'manager' || restrictedRole === 'tbn-parent') {
    if (isTbn) {
      return [
        { name: 'Driver Daily Report', route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
        { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn', description: 'Driver activity and hours across multiple days.' },
      ]
    }
    return [
      { name: 'Driver Daily Report', route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
      { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report', description: 'Driver activity and hours across multiple days.' },
    ]
  }
  if (isTbn) {
    return [
      { name: 'Daily On-Time Report', route: 'actuals_perf_report_tbn', description: 'Compare planned booking times against actual on-time performance by day.' },
      { name: 'Multiday On-Time Report', route: 'actuals_multiday_otp_report_tbn', description: 'On-time performance trends across a custom date range.' },
      { name: 'Driver On-Time % Report', route: 'actuals_on_time_report_tbn', description: 'On-time gates (to yard, depart yard) by driver for a date range.' },
      { name: 'Pickup/Dropoff Report', route: 'actuals_billing_report_tbn', description: 'Pickup and dropoff event details per booking.' },
      { name: 'Start Events Report', route: 'actuals_start_events_report_tbn', description: 'Vehicle start events matched to scheduled bookings.' },
      { name: 'Driver Daily Report', route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
      { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn', description: 'Driver activity and hours across multiple days.' },
      { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report_tbn', description: 'Aggregated payroll totals by driver for a date range.' },
    ]
  }
  return [
    { name: 'Daily On-Time Report', route: 'actuals_perf_report', description: 'Compare planned booking times against actual on-time performance by day.' },
    { name: 'Multiday On-Time Report', route: 'actuals_multiday_otp_report', description: 'On-time performance trends across a custom date range.' },
    { name: 'Driver On-Time % Report', route: 'actuals_on_time_report', description: 'On-time gates (to yard, depart yard, to pickup) by driver for a date range.' },
    { name: 'Mileage By Booking', route: 'actuals_mileage_by_booking_report', description: 'Per-booking mileage breakdown for accurate client billing.' },
    { name: 'Billing Report', route: 'actuals_billing_report', description: 'Summarised billing data based on completed actuals.' },
    { name: 'Vehicle Start/Stop', route: 'actuals_vehicle_report', description: 'Vehicle engine start and stop events per booking.' },
    { name: 'Driver Daily Report', route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
    { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report', description: 'Driver activity and hours across multiple days.' },
    { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report', description: 'Aggregated payroll totals by driver for a date range.' },
  ]
}

export function getPlannedCards(ctx) {
  const { restrictedRole, isTbn } = ctx
  if (restrictedRole === 'garage') {
    return [{ name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly', description: 'Weekly view of vehicle fleet utilisation.' }]
  }
  if (isTbn) {
    return [
      { name: 'Driver Utilization Daily', route: 'planned_drivers_daily_tbn', description: 'Daily view of driver utilisation against planned bookings.' },
      { name: 'Driver Utilization Weekly', route: 'planned_drivers_weekly_util_tbn', description: 'Weekly driver utilisation across the fleet.' },
      { name: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_tbn', description: 'Monthly breakdown of driver type utilisation.' },
      { name: 'Driver Dispatch Weekly', route: 'planned_drivers_weekly_dispatch_tbn', description: 'Weekly driver dispatch schedule.' },
      { name: 'Vehicle Utilization Daily', route: 'planned_vehicle_daily_tbn', description: 'Daily vehicle usage compared to planned schedule.' },
      { name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly_tbn', description: 'Weekly view of vehicle fleet utilisation.' },
      { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly_tbn', description: 'Monthly fleet utilisation by vehicle type.' },
      { name: 'Vehicle Departure Board', route: 'trip_sheet_tbn', description: 'Scheduled departure times and vehicle assignments.' },
      { name: 'Booking Dispatch Board', route: 'dispatch_board_tbn', description: 'Live dispatch board for booking management.' },
      { name: 'Booking Monthly View', route: 'booking_monthly_view_tbn', description: 'Monthly calendar view of all bookings.' },
    ]
  }
  return [
    { name: 'Driver Board', route: 'driver_board', description: 'Real-time driver assignments and availability board.' },
    { name: 'Driver Utilization Daily', route: 'planned_drivers_daily', description: 'Daily view of driver utilisation against planned bookings.' },
    { name: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_cm', description: 'Monthly breakdown of driver type utilisation.' },
    { name: 'Vehicle Utilization Daily', route: 'planned_vehicle_daily', description: 'Daily vehicle usage compared to planned schedule.' },
    { name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly', description: 'Weekly view of vehicle fleet utilisation.' },
    { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly', description: 'Monthly fleet utilisation by vehicle type.' },
    { name: 'Vehicle Departure Board', route: 'trip_sheet', description: 'Scheduled departure times and vehicle assignments.' },
  ]
}

export function getOtpCards(ctx) {
  const { isTbn } = ctx
  const cards = [
    { name: 'On-Time Route History', route: 'vehicle_route_history', description: 'Historical route data for each vehicle.' },
    { name: 'Route Addresses', route: 'route_addresses', description: 'Address list for configured routes.' },
    { name: 'On-Time Nightly Run History', route: 'report_otp', description: 'Nightly OTP report run history and results.' },
    { name: 'On-Time Results History', route: 'otp_history', description: 'Historical OTP results with trends over time.' },
  ]
  if (!isTbn) {
    cards.splice(1, 0, { name: 'Route Assignments', route: 'route_assignments', description: 'Route-to-vehicle assignment history.' })
  }
  return cards
}

export function getMiscCards(ctx) {
  const { user, restrictedRole, isTbn, telematics_type, features } = ctx
  if (restrictedRole === 'tbn-parent') {
    return [{ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' }]
  }
  const cards = []
  if (!isTbn) {
    cards.push(
      { name: 'Unplanned Vehicle Activity', route: 'report_unplanned_vehicle', description: 'Vehicles that moved outside of scheduled bookings.' },
      { name: 'Unplanned Driver Activity', route: 'report_unplanned_hos', description: 'Driver activity recorded outside planned bookings.' },
    )
  }
  cards.push({ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' })
  if (user.tenant === 'jeffersonlines-d637fdad') {
    cards.unshift(
      { name: 'Bus Tracker', route: 'bus_tracker', description: 'Live bus tracking view for Jefferson Lines.' },
      { name: 'Arrive/Depart Dashboard', route: 'arrivals_departures_board', description: 'Arrivals and departures live dashboard.' },
    )
  }
  if (isTbn || telematics_type === 'SAMSARA') {
    cards.push({ name: 'HOS Event History', route: 'hos_history_report', description: 'Hours of service events for equipped vehicles.' })
  }
  if (isTbn && features.tbn_tyler_route_sync_enabled) {
    cards.push({ name: 'Tyler Route Sync', route: 'tyler_route_processor', description: 'Process and sync Tyler route data.' })
  }
  if (features.vehicle_status_management_enabled) {
    cards.push({ name: 'Vehicle Status History', route: 'vehicle_status_history', description: 'Historical vehicle status and inspection records.' })
  }
  if (features.views_reports_converter_enabled) {
    cards.push({ name: 'Report Converter', route: 'report_converter', description: 'Convert report formats for third-party system compatibility.' })
  }
  if (!isTbn && features.vehicle_status_management_maint_summary_enabled) {
    cards.push({ name: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary', description: 'Summary of vehicle maintenance activities and costs.' })
  }
  if (isTbn && user.tenant === 'mtrwestern-tbn-3db5da5g7') {
    cards.push({ name: 'Hours By Client Report', route: 'actuals_hours_by_client_report_tbn', description: 'Total hours worked grouped by client.' })
    cards.push({ name: 'Hours By Booking Report', route: 'actuals_hours_by_booking_report_tbn', description: 'Hours breakdown per individual booking.' })
  }
  if (isTbn && (user.tenant === 'anderson-759g468f' || user.role === 'admin')) {
    cards.push({ name: 'Intrastate Booking Report', route: 'intrastate_booking_report_tbn', description: 'Report on intrastate travel bookings.' })
  }
  if (isTbn && (user.tenant === 'haletrans-31f47ab3' || user.role === 'admin')) {
    cards.push({ name: 'Daily Dispatch', route: 'hale_daily_dispatch_report_tbn', description: 'Daily dispatch sheet split by garage, with departing and already-out trips.' })
  }
  return cards
}

export function getAllReportCards(ctx) {
  const cards = []
  if (ctx.showActuals) cards.push(...getActualsCards(ctx))
  if (ctx.showPlanned) cards.push(...getPlannedCards(ctx))
  if (ctx.showOtp) cards.push(...getOtpCards(ctx))
  if (ctx.showMisc) cards.push(...getMiscCards(ctx))
  return cards
}

// Full catalog for site map teasers — includes reports gated by feature flags.
export function getTeaserReportCards(ctx) {
  const { tenant_type, isCmFleet, isFasttrak } = ctx
  if (isCmFleet || isFasttrak || tenant_type === 'FLEET') return []
  const teaserCtx = {
    ...ctx,
    restrictedRole: null,
    showActuals: true,
    showPlanned: true,
    showOtp: true,
    showMisc: true,
  }
  return [
    ...getActualsCards(teaserCtx),
    ...getPlannedCards(teaserCtx),
    ...getOtpCards(teaserCtx),
    ...getMiscCards(teaserCtx),
  ]
}
